import React, { useState, useEffect, useMemo } from 'react';

import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { Table, Button, Space, Modal, Form, Select, InputNumber, Switch, Tag, message, Popconfirm, Empty, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

function MerchantInventoryTypesList({
  merchantId: initialMerchantId,
  inventoryTypes = [],
  merchantInventoryTypeService,
  role = 'admin',
  allowCreate = false,
}) {
  const { t } = useTranslation();
  const [selectedMerchantId, setSelectedMerchantId] = useState(initialMerchantId);
  const [merchantInventoryTypes, setMerchantInventoryTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // 當 operator 切換 merchant 時，清空已選擇的 merchantId
  useEffect(() => {
    if (role === 'operator' && initialMerchantId) {
      setSelectedMerchantId(initialMerchantId);
    }
  }, [initialMerchantId, role]);

  // 載入 merchant inventory types
  const loadMerchantInventoryTypes = async () => {
    if (!selectedMerchantId) return;

    setLoading(true);
    try {
      const params = role === 'operator' ? { merchantId: selectedMerchantId } : {};
      const data = await merchantInventoryTypeService.fetchList(params);

      if (data.success) {
        setMerchantInventoryTypes(data.data || []);
      } else {
        message.error(data.message || t('common.message.fetch_failed'));
      }
    } catch (error) {
      console.error('Load error:', error);
      message.error(t('common.message.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMerchantInventoryTypes();
  }, [selectedMerchantId]);

  // 打開新增 Modal
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      expiration_mechanism: 'none',
      expiration_notification_enabled: false,
      expiration_notification_days: 7,
      ignore_sales_channel: false,
    });
    setIsModalVisible(true);
  };

  // 打開編輯 Modal
  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      inventory_type_id: record.inventory_type_id,
      expiration_mechanism: record.expiration_mechanism,
      expiration_ratio: record.expiration_ratio,
      days_before_expiration: record.days_before_expiration,
      expiration_notification_enabled: record.expiration_notification_enabled,
      expiration_notification_days: record.expiration_notification_days ?? 7,
      inventory_age_alert_days: record.inventory_age_alert_days,
      ignore_sales_channel: record.ignore_sales_channel,
    });
    setIsModalVisible(true);
  };

  // 關閉 Modal
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  // 提交表單
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const params = role === 'operator' ? { merchantId: selectedMerchantId } : {};
      let data;

      if (editingRecord) {
        // 更新
        data = await merchantInventoryTypeService.update(editingRecord.id, { merchant_inventory_type: values }, params);
      } else {
        // 新增（只有 Operator 允許）
        data = await merchantInventoryTypeService.create({ merchant_inventory_type: values }, params);
      }

      if (data.success) {
        message.success(
          editingRecord
            ? t('common.message.update_success')
            : t('common.message.create_success')
        );
        handleCancel();
        loadMerchantInventoryTypes();
      } else if (data.errors) {
        const errorMessages = Object.entries(data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('\n');
        message.error(errorMessages);
      } else {
        message.error(data.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      if (error.errorFields) {
        message.error(t('common.message.please_check_form'));
      } else {
        console.error('Save error:', error);
        message.error(t('common.error.operation_failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // 刪除
  const handleDelete = async (record) => {
    try {
      const params = role === 'operator' ? { merchantId: selectedMerchantId } : {};
      const data = await merchantInventoryTypeService.delete(record.id, params);

      if (data.success) {
        message.success(t('common.message.delete_success'));
        loadMerchantInventoryTypes();
      } else {
        message.error(data.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      message.error(t('common.error.operation_failed'));
    }
  };

  // 已使用的 inventory type IDs
  const usedInventoryTypeIds = useMemo(() => {
    if (editingRecord) {
      return merchantInventoryTypes
        .filter(sit => sit.id !== editingRecord.id)
        .map(sit => sit.inventory_type_id);
    }
    return merchantInventoryTypes.map(sit => sit.inventory_type_id);
  }, [merchantInventoryTypes, editingRecord]);

  // 可選的 inventory types
  const availableInventoryTypes = useMemo(() => {
    return inventoryTypes.filter(it => !usedInventoryTypeIds.includes(it.id));
  }, [inventoryTypes, usedInventoryTypeIds]);

  const columns = useMemo(() => [
    {
      title: t('models.inventory_type.code'),
      dataIndex: ['inventory_type', 'code'],
      key: 'code',
      width: 120,
    },
    {
      title: t('models.inventory_type.name'),
      dataIndex: ['inventory_type', 'name'],
      key: 'name',
      width: 150,
    },
    {
      title: t('models.merchant_inventory_type.expiration_mechanism'),
      dataIndex: 'expiration_mechanism',
      key: 'expiration_mechanism',
      width: 120,
      render: (value) => t(`enums.expiration_mechanism.${value}`),
    },
    {
      title: t('models.merchant_inventory_type.expiration_ratio'),
      dataIndex: 'expiration_ratio',
      key: 'expiration_ratio',
      width: 100,
      render: (value) => value ? `${value}%` : '-',
    },
    {
      title: t('models.merchant_inventory_type.days_before_expiration'),
      dataIndex: 'days_before_expiration',
      key: 'days_before_expiration',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: t('models.merchant_inventory_type.expiration_notification_enabled'),
      key: 'notification',
      width: 130,
      render: (_, record) => {
        if (!record.expiration_notification_enabled) return '-';
        const days = record.expiration_notification_days ?? 7;
        return <Tag color="blue">{t('models.merchant_inventory_type.expiration_notification_days_tag', { days })}</Tag>;
      },
    },
    {
      title: t('models.merchant_inventory_type.inventory_age_alert_days'),
      dataIndex: 'inventory_age_alert_days',
      key: 'inventory_age_alert_days',
      width: 120,
      render: (value) => value && value > 0 ? `${value} ${t('common.unit.days')}` : '—',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.button.edit')}
          </Button>
          {allowCreate && (
            <Popconfirm
              title={t('common.message.confirm_delete')}
              onConfirm={() => handleDelete(record)}
              okText={t('common.button.confirm')}
              cancelText={t('common.button.cancel')}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                {t('common.button.delete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ], [t, allowCreate, selectedMerchantId, role, merchantInventoryTypeService]);

  const expirationMechanism = Form.useWatch('expiration_mechanism', form);
  const notificationEnabled = Form.useWatch('expiration_notification_enabled', form);

  const handleExpirationMechanismChange = (value) => {
    if (value === 'none') {
      form.setFieldsValue({
        expiration_ratio: null,
        days_before_expiration: null,
        expiration_notification_enabled: false,
        expiration_notification_days: 7,
      });
    } else if (value === 'by_ratio') {
      form.setFieldsValue({ days_before_expiration: null });
    } else if (value === 'by_days') {
      form.setFieldsValue({ expiration_ratio: null });
    }
  };

  const isNotificationFieldActive = notificationEnabled && expirationMechanism !== 'none';

  // 引導前往庫存類型設定頁面（Operator only）
  const handleGoToInventoryTypeSettings = () => {
    window.open('/operator/data/inventory_types', '_blank');
  };

  // 檢測是否沒有任何可用的 inventoryTypes（organization 層級）
  const hasNoInventoryTypes = inventoryTypes.length === 0;

  return (
    <>
      <div>
        {/* 選擇了 Merchant 後顯示表格 */}
        {(role === 'admin' || selectedMerchantId) && (
          <>
            {/* 如果 organization 沒有定義任何 inventoryTypes，顯示引導 */}
            {hasNoInventoryTypes && role === 'operator' ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical" size="small">
                    <Text type="secondary">
                      {t('shared.merchant_inventory_types.no_inventory_types_defined')}
                    </Text>
                    <Button
                      type="primary"
                      icon={<SettingOutlined />}
                      onClick={handleGoToInventoryTypeSettings}
                    >
                      {t('shared.merchant_inventory_types.go_to_inventory_type_settings')}
                    </Button>
                  </Space>
                }
                style={{ padding: '40px 0' }}
              />
            ) : (
              <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  {allowCreate && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAdd}
                      disabled={availableInventoryTypes.length === 0}
                    >
                      {t('common.button.add')}
                    </Button>
                  )}
                </div>

                <Table
                  columns={columns}
                  dataSource={merchantInventoryTypes}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => t('common.table.total_items', { count: total }),
                  }}
                  locale={{
                    emptyText: t('common.table.no_data'),
                  }}
                  scroll={{ x: 1320 }}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* 新增/編輯 Modal */}
      <Modal
        title={
          editingRecord
            ? `${t('common.button.edit')} ${t('models.merchant_inventory_type._')}`
            : `${t('common.button.add')} ${t('models.merchant_inventory_type._')}`
        }
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={saving}
        okText={t('common.button.save')}
        cancelText={t('common.button.cancel')}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 24 }}
        >
          <Form.Item
            name="inventory_type_id"
            label={t('models.merchant_inventory_type.inventory_type')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Select
              placeholder={t('common.placeholder.please_select')}
              disabled={!!editingRecord}
              showSearch
              optionFilterProp="label"
              options={
                editingRecord
                  ? [{ value: editingRecord.inventory_type_id, label: `${editingRecord.inventory_type.name} | ${editingRecord.inventory_type.code}` }]
                  : availableInventoryTypes.map(it => ({
                    value: it.id,
                    label: `${it.name} | ${it.code}`,
                  }))
              }
            />
          </Form.Item>

          <Form.Item
            name="expiration_mechanism"
            label={t('models.merchant_inventory_type.expiration_mechanism')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Select
              placeholder={t('common.placeholder.please_select')}
              onChange={handleExpirationMechanismChange}
              options={[
                { value: 'none', label: t('enums.expiration_mechanism.none') },
                { value: 'by_days', label: t('enums.expiration_mechanism.by_days') },
                { value: 'by_ratio', label: t('enums.expiration_mechanism.by_ratio') },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="expiration_ratio"
            label={t('models.merchant_inventory_type.expiration_ratio')}
            rules={[{ required: expirationMechanism === 'by_ratio', message: t('common.validation.required') }]}
          >
            <InputNumber
              min={0}
              max={100}
              placeholder={t('common.placeholder.please_enter')}
              style={{ width: '100%' }}
              addonAfter="%"
              disabled={expirationMechanism !== 'by_ratio'}
            />
          </Form.Item>

          <Form.Item
            name="days_before_expiration"
            label={t('models.merchant_inventory_type.days_before_expiration')}
            rules={[{ required: expirationMechanism === 'by_days', message: t('common.validation.required') }]}
          >
            <InputNumber
              min={0}
              placeholder={t('common.placeholder.please_enter')}
              style={{ width: '100%' }}
              addonAfter={t('common.unit.days')}
              disabled={expirationMechanism !== 'by_days'}
            />
          </Form.Item>

          <Form.Item
            name="expiration_notification_enabled"
            label={t('models.merchant_inventory_type.expiration_notification_enabled')}
            tooltip={t('models.merchant_inventory_type.expiration_notification_enabled_tooltip')}
            valuePropName="checked"
          >
            <Switch disabled={expirationMechanism === 'none'} />
          </Form.Item>

          <Form.Item
            name="expiration_notification_days"
            label={t('models.merchant_inventory_type.expiration_notification_days')}
            tooltip={t('models.merchant_inventory_type.expiration_notification_days_tooltip')}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>{t('models.merchant_inventory_type.expiration_notification_days_description')}</Text>}
            rules={[{ required: isNotificationFieldActive, message: t('common.validation.required') }]}
          >
            <InputNumber
              min={0}
              max={365}
              placeholder="7"
              style={{ width: '100%' }}
              addonAfter={t('common.unit.days')}
              disabled={!isNotificationFieldActive}
            />
          </Form.Item>

          <Form.Item
            name="inventory_age_alert_days"
            label={t('models.merchant_inventory_type.inventory_age_alert_days')}
            tooltip={t('models.merchant_inventory_type.inventory_age_alert_days_tooltip')}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>
              {t('models.merchant_inventory_type.inventory_age_alert_days_description')}
            </Text>}
            rules={[{ type: 'number', min: 1, message: t('common.validation.min_value', { min: 1 }) }]}
          >
            <InputNumber
              min={1}
              placeholder={t('models.merchant_inventory_type.inventory_age_alert_days_placeholder')}
              style={{ width: '100%' }}
              addonAfter={t('common.unit.days')}
            />
          </Form.Item>

          <Form.Item
            name="ignore_sales_channel"
            label={t('models.merchant_inventory_type.ignore_sales_channel')}
            tooltip={t('models.merchant_inventory_type.ignore_sales_channel_tooltip')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default MerchantInventoryTypesList;
